import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DContext from "../../context/DContext";

import {
  LIGHT_LOGO_URL as companyLogo,
  ROLE_VISIBILITY,
  isVisibleForRole,
} from "../../config/siteConfig";
import OffcanvasComponent from "../offcanvas/OffCanvasComponent";
import { capitalizeFirstLetter } from "../../helper/stringMethods";
import { notificationsService } from "../../services/notifications.service";
import { supplierWalletService } from "../../services/supplier-wallet.service";
import Icon from "../common/Icon";
import { BRAND_NAME as COMPANY_NAME } from "../../constants/brand";

const NOTIFICATION_POLL_MS = 60000;


const timeAgo = (value) => {
  if (!value) return "";
  const diff = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} day ago`;
};

const Header = () => {
  const navigate = useNavigate();

  const { authState, authDispatch, logout } = useContext(DContext);
  const { user, user_role } = authState;

  const handlelogout = () => {
    logout();
    authDispatch({ type: "LOGOUT", payload: "logging out" });
    navigate("/");
  };

  const toggleSidebar = () => {
    document.body.classList.toggle("toggle-sidebar");
  };

  const location = useLocation();
  const bellShake = isVisibleForRole(ROLE_VISIBILITY.header.bellShake, authState);
  const bellLatestList = isVisibleForRole(ROLE_VISIBILITY.header.bellLatestList, authState);
  const bellClickRedirect = isVisibleForRole(ROLE_VISIBILITY.header.bellClickRedirect, authState);
  const showWalletBalance = isVisibleForRole(ROLE_VISIBILITY.header.walletBalance, authState);
  const needsNotificationData = bellShake || bellLatestList;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadNotifications = async () => {
    if (!needsNotificationData) return;
    try {
      const [latest, unread] = await Promise.all([
        //notificationsService.list({ page: 1, limit: 5 }),
        notificationsService.list({ page: 1, limit: 1, is_read: false }),
      ]);
      if (latest?.success) setNotifications(latest.resData?.data || []);
      if (unread?.success) setUnreadCount(unread.resData?.pagination?.total || 0);
    } catch (err) {
      // header bell is non-critical; ignore failures
    }
  };

  const supplierCode = authState?.userProfile?.supplier_code;
  const [availableBalance, setAvailableBalance] = useState(null);

  const loadWalletBalance = async () => {
    if (!showWalletBalance || !supplierCode) return;
    try {
      const res = await supplierWalletService.balance(supplierCode);
      if (res?.success) setAvailableBalance(res.resData?.wallet?.available_balance ?? null);
    } catch (err) {
      // wallet chip is non-critical; ignore failures
    }
  };

  useEffect(() => {
    loadNotifications();
    loadWalletBalance();
  }, [location.pathname, needsNotificationData, showWalletBalance, supplierCode]);

  useEffect(() => {
    const timer = setInterval(() => { loadNotifications(); loadWalletBalance(); }, NOTIFICATION_POLL_MS);
    return () => clearInterval(timer);
  }, [needsNotificationData, showWalletBalance, supplierCode]);

  const handleNotificationClick = async (n) => {
    if (n?.is_read) return;
    await notificationsService.markRead(n.id);
    loadNotifications();
  };

  const [showCanvase, setShowCanvas] = useState(false);

  const handleToggleCanvas = () => {
    if (showCanvase === true) {
      setShowCanvas(false);
    } else {
      setShowCanvas(true);
    }
  };

  const hasUnread = bellShake && unreadCount > 0;
  const avatarUrl = `https://ui-avatars.com/api/?name=${user?.name}&size=75&background=0E6A96&color=fff`;
  const roleLabel = user?.role?.role_type;

  return (
    <>
      <style>{`
        #header { --hdr-line: rgba(14,106,150,.16); --hdr-hover: rgba(14,106,150,.07); --hdr-muted: #5b7083; --hdr-ring: var(--btn-bg); }
        #header .hdr-btn { height: 36px; min-width: 36px; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
                           padding: 0 8px; border-radius: 8px; border: 1px solid var(--hdr-line); background: transparent;
                           color: var(--hdr-text); text-decoration: none; transition: background-color .15s, border-color .15s; }
        #header .hdr-btn:hover { background: var(--hdr-hover); border-color: rgba(14,106,150,.28); color: var(--btn-bg); }
        #header .hdr-btn:focus-visible { outline: 2px solid var(--hdr-ring); outline-offset: 2px; }
        #header .hdr-btn.is-ghost { border-color: transparent; }
        #header .hdr-btn.is-ghost:hover { border-color: transparent; }
        #header .hdr-logo { width: auto; margin: 0; }
        #header .hdr-logo img { height: 28px; max-height: 28px; margin: 0; object-fit: contain; }
        #header .hdr-logo .hdr-logo-text { font-family: inherit; font-size: 18px; font-weight: 700; letter-spacing: .02em; line-height: 1; color: var(--btn-bg); }
        #header .hdr-divider { width: 1px; height: 24px; background: var(--hdr-line); }
        #header .hdr-badge { position: absolute; top: -5px; right: -5px; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 999px;
                             background: #ef4444; color: #fff; font-size: 10px; font-weight: 700; line-height: 18px; text-align: center;
                             border: 2px solid var(--hdr-bg); }
        #header .hdr-menu { border: 1px solid #e5e7eb !important; border-radius: 12px; padding: 6px; box-shadow: 0 10px 30px rgba(15,23,42,.12); }
        #header .hdr-menu .dropdown-item { display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 10px; border-radius: 8px;
                                           font-size: 13px; color: #374151; background: transparent; transition: background-color .15s; }
        #header .hdr-menu .dropdown-item:hover, #header .hdr-menu .dropdown-item:focus-visible { background: #f3f4f6; color: #111827; }
        #header .hdr-menu .dropdown-item.is-danger { color: #dc2626; }
        #header .hdr-menu .dropdown-item.is-danger:hover { background: #fef2f2; color: #b91c1c; }
        #header .hdr-menu .dropdown-item i { margin: 0; color: inherit; }
        #header .hdr-notif { display: flex; align-items: flex-start; gap: 12px; padding: 8px 10px; border-radius: 8px; cursor: pointer; transition: background-color .15s; }
        #header .hdr-notif:hover { background: #f3f4f6; }
        #header .hdr-notif.is-unread { background: #f0f7fc; }
        @keyframes bell-shake { 0%,100% { transform: rotate(0); } 10%,30%,50%,70%,90% { transform: rotate(-14deg); } 20%,40%,60%,80% { transform: rotate(14deg); } }
        .bell-shake { animation: bell-shake 1.2s ease-in-out infinite; transform-origin: top center; }
        @media (prefers-reduced-motion: reduce) { .bell-shake { animation: none; } }
      `}</style>
      <header
        id="header"
        className="header fixed-top d-flex align-items-center"
        style={{
          background: "var(--hdr-bg)",
          borderBottom: "1px solid #d4e6f2",
          boxShadow: "0 2px 12px rgba(14,106,150,0.08)",
          height: "var(--header-h)",
          padding: "0 16px",
        }}
      >
        {/* Toggle + Logo */}
        <div className="d-flex align-items-center gap-3" style={{ flexShrink: 0 }}>
          <button
            type="button"
            id="toggle-sidebar-btn"
            className="hdr-btn is-ghost"
            onClick={toggleSidebar}
            aria-label="Toggle navigation menu"
            aria-controls="sidebar"
            title="Toggle menu"
          >
            <Icon name="list" size={22} />
          </button>
          <div className="logo hdr-logo d-flex align-items-center">
            <Link to="/" className="d-flex align-items-center gap-2 text-decoration-none" aria-label={`${COMPANY_NAME} home`}>
              <img src={companyLogo} alt="" />
              <span className="hdr-logo-text">{COMPANY_NAME}</span>
            </Link>
          </div>
        </div>

        {/* Right Nav */}
        <nav className="header-nav ms-auto" aria-label="Account">
          <ul className="d-flex align-items-center gap-2 mb-0 list-unstyled">
            {/* Wallet */}
            {showWalletBalance && <li className="wallet-balance">
              <div className="hdr-btn px-3" title="Wallet balance" style={{ cursor: "default", fontSize: 13, fontWeight: 600 }}>
                <Icon name="wallet2" size="sm" style={{ color: "var(--hdr-muted)" }} />
                <span className="d-none d-sm-inline" style={{ color: "var(--hdr-muted)", fontWeight: 500 }}>Wallet</span>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                  {availableBalance === null ? "-" : Number(availableBalance).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </li>}

            {/* Notifications */}
            <li className="nav-item dropdown">
              <a
                className="hdr-btn position-relative"
                href="#"
                role="button"
                aria-label={hasUnread ? `Notifications, ${unreadCount} unread` : "Notifications"}
                title="Notifications"
                data-bs-toggle={bellLatestList && !bellClickRedirect ? "dropdown" : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  if (bellClickRedirect) navigate("/notifications");
                }}
              >
                <Icon name="bell" size="md" className={hasUnread ? "bell-shake" : ""} />
                {hasUnread && <span className="hdr-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>}
              </a>

              {bellLatestList && !bellClickRedirect && <ul
                className="dropdown-menu dropdown-menu-end hdr-menu mt-2"
                style={{ minWidth: "320px" }}
              >
                <li className="d-flex align-items-center justify-content-between px-2 py-2">
                  <span style={{ fontSize: "14px", fontWeight: "600", color: "#111827" }}>Notifications</span>
                  {unreadCount > 0 && (
                    <span style={{ fontSize: "12px", color: "#6b7280" }}>{unreadCount} unread</span>
                  )}
                </li>

                {notifications.length === 0 && (
                  <li className="text-center py-4" style={{ fontSize: "13px", color: "#6b7280" }}>
                    <Icon name="bell-slash" size="lg" style={{ color: "#9ca3af", display: "flex", margin: "0 auto 8px" }} />
                    You're all caught up
                  </li>
                )}
                {notifications.map((n) => (
                  <li key={n.id}>
                    <div
                      className={`hdr-notif${n.is_read ? "" : " is-unread"}`}
                      onClick={() => handleNotificationClick(n)}
                    >
                      <span
                        className="d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 32, height: 32, borderRadius: "50%", background: n.is_read ? "#f3f4f6" : "#d6ebf7", color: n.is_read ? "#9ca3af" : "var(--btn-bg)" }}
                      >
                        <Icon name="bell" size="sm" />
                      </span>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <p className="mb-0" style={{ fontSize: "13px", fontWeight: n.is_read ? 500 : 600, color: "#111827" }}>{n.title}</p>
                        <p className="mb-0" style={{ fontSize: "12px", color: "#6b7280" }}>{n.message}</p>
                        <span style={{ fontSize: "11px", color: "#9ca3af" }}>{timeAgo(n.created_at)}</span>
                      </div>
                      {!n.is_read && <span className="flex-shrink-0 mt-2" style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--btn-bg)" }}></span>}
                    </div>
                  </li>
                ))}

                <li className="mt-1 pt-1" style={{ borderTop: "1px solid #f3f4f6" }}>
                  <Link
                    to="/notifications"
                    className="d-block text-center text-decoration-none py-2"
                    style={{ fontSize: "13px", color: "var(--btn-bg)!important", fontWeight: "600" }}
                  >
                    View all notifications
                  </Link>
                </li>
              </ul>}
            </li>

            <li className="d-none d-md-block" aria-hidden="true"><span className="hdr-divider d-block"></span></li>

            {/* Profile */}
            <li className="nav-item dropdown">
              <a
                className="hdr-btn is-ghost text-decoration-none"
                href="#"
                role="button"
                aria-label="Account menu"
                data-bs-toggle="dropdown"
                style={{ padding: "0 6px" }}
              >
                <img
                  src={avatarUrl}
                  alt=""
                  className="rounded-circle"
                  style={{ width: "28px", height: "28px", objectFit: "cover", flexShrink: 0 }}
                />
                <span className="d-none d-md-flex flex-column text-start" style={{ lineHeight: 1.2 }}>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--hdr-text)" }}>
                    {capitalizeFirstLetter(user?.name)}
                  </span>
                  {roleLabel && (
                    <span style={{ fontSize: "11px", color: "var(--hdr-muted)" }}>{capitalizeFirstLetter(roleLabel)}</span>
                  )}
                </span>
                <Icon name="chevron-down" size={14} className="d-none d-md-inline-flex" style={{ color: "var(--hdr-muted)" }} />
              </a>

              <ul className="dropdown-menu dropdown-menu-end hdr-menu mt-2" style={{ minWidth: "240px" }}>
                <li className="px-2 py-2">
                  <div className="d-flex align-items-center gap-2">
                    <img src={avatarUrl} alt="" className="rounded-circle" style={{ width: "40px", height: "40px", flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <p className="mb-0 text-truncate" style={{ fontSize: "14px", fontWeight: "600", color: "#111827" }}>
                        {capitalizeFirstLetter(user?.name)}
                      </p>
                      <div className="d-flex gap-1 mt-1">
                        {authState?.userProfile?.supplier_code && (
                          <span style={{ fontSize: "11px", fontWeight: "500", background: "#f3f4f6", color: "#4b5563", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "0 6px" }}>
                            {authState?.userProfile?.supplier_code.toUpperCase()}
                          </span>
                        )}
                        {roleLabel && (
                          <span style={{ fontSize: "11px", fontWeight: "500", background: "#f0f7fc", color: "var(--btn-bg)", border: "1px solid #d6ebf7", borderRadius: "6px", padding: "0 6px" }}>
                            {roleLabel.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>

                <li><hr className="dropdown-divider my-1" style={{ borderColor: "#f3f4f6" }} /></li>

                {[
                  { to: "/profile", icon: "bi-person", label: "My Profile" },
                ].map((item, i) => (
                  <li key={i}>
                    <Link to={item.to} className="dropdown-item">
                      <Icon name={item.icon} size="sm" />
                      {item.label}
                    </Link>
                  </li>
                ))}

                <li>
                  <Link to="#" onClick={handleToggleCanvas} className="dropdown-item">
                    <Icon name="gear" size="sm" />
                    Account Settings
                  </Link>
                </li>

                <li>
                  <Link to="/faq" className="dropdown-item">
                    <Icon name="question-circle" size="sm" />
                    Need Help?
                  </Link>
                </li>

                <li><hr className="dropdown-divider my-1" style={{ borderColor: "#f3f4f6" }} /></li>

                <li>
                  <button type="button" onClick={handlelogout} className="dropdown-item is-danger">
                    <Icon name="box-arrow-right" size="sm" />
                    Sign Out
                  </button>
                </li>
              </ul>
            </li>
          </ul>
        </nav>
      </header>

      <OffcanvasComponent
        show={showCanvase}
        toggleCanvas={handleToggleCanvas}
      />
    </>
  );
};

export default Header;
